import type { CSSProperties } from 'react'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client'

const MEALS = ['breakfast', 'lunch', 'dinner', 'snack'] as const
type Meal = (typeof MEALS)[number]

const MEAL_LABELS: Record<Meal, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
  snack: 'Snack',
}

// Return today's date in local timezone as YYYY-MM-DD
function localToday() {
  const d = new Date()
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-')
}

function formatDate(iso: string) {
  const today = localToday()
  const [ty, tm, td] = today.split('-').map(Number)
  const yesterday = new Date(Date.UTC(ty, tm - 1, td - 1)).toISOString().slice(0, 10)

  if (iso === today) return 'Today'
  if (iso === yesterday) return 'Yesterday'
  // Parse as local noon to avoid any timezone boundary issues in toLocaleDateString
  const d = new Date(iso + 'T12:00:00')
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })
}

// Pure UTC arithmetic — avoids DST/timezone drift
function addDays(iso: string, n: number) {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10)
}

// Circular progress ring
function CalorieRing({
  consumed,
  target,
}: {
  consumed: number
  target: number | null
}) {
  const size = 140
  const stroke = 10
  const r = (size - stroke) / 2
  const circ = 2 * Math.PI * r
  const pct = target ? Math.min(consumed / target, 1) : 0
  const dash = pct * circ
  const over = target ? consumed > target : false

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#27272a"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={over ? '#f87171' : '#22d3ee'}
          strokeWidth={stroke}
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.4s ease' }}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span style={{ fontSize: '1.5rem', fontWeight: 700, color: over ? '#f87171' : '#fafafa' }}>
          {Math.round(consumed)}
        </span>
        <span style={{ fontSize: '0.65rem', color: '#71717a' }}>
          {target ? `/ ${target} kcal` : 'kcal'}
        </span>
      </div>
    </div>
  )
}

// Macro bar
function MacroBar({
  label,
  value,
  target,
  color,
}: {
  label: string
  value: number
  target: number | null
  color: string
}) {
  const pct = target ? Math.min(value / target, 1) : 0
  return (
    <div style={{ flex: 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: '0.7rem', color: '#a1a1aa' }}>{label}</span>
        <span style={{ fontSize: '0.7rem', color: '#71717a' }}>
          {Math.round(value)}{target ? `/${target}` : ''}g
        </span>
      </div>
      <div style={{ height: 4, background: '#27272a', borderRadius: 2, overflow: 'hidden' }}>
        <div
          style={{
            height: '100%',
            width: `${pct * 100}%`,
            background: color,
            borderRadius: 2,
            transition: 'width 0.4s ease',
          }}
        />
      </div>
    </div>
  )
}

export default function DayPage() {
  const [date, setDate] = useState(localToday)
  const isToday = date === localToday()
  const [customWater, setCustomWater] = useState('')
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['summary', 'day', date],
    queryFn: () => api.summary.day(date),
  })

  const { data: weightLogs = [] } = useQuery({
    queryKey: ['logs', 'weight'],
    queryFn: () => api.logs.weight.list(90),
  })

  const todayWeight = weightLogs.find((w: any) => w.log_date === date)
  const [weightInput, setWeightInput] = useState('')

  const logWeight = useMutation({
    mutationFn: (weight_kg: number) => api.logs.weight.add({ log_date: date, weight_kg }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['logs', 'weight'] })
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      setWeightInput('')
    },
  })

  const deleteLog = useMutation({
    mutationFn: (logId: string) => api.logs.food.remove(logId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['summary', 'day', date] }),
  })

  const addWater = useMutation({
    mutationFn: (amount_ml: number) => api.logs.water.add({ log_date: date, amount_ml }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['summary', 'day', date] }),
  })

  const totals = data?.totals ?? { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, fibre_g: 0, water_ml: 0 }
  const targets = data?.targets ?? null
  const waterMl = data?.water_ml ?? 0

  return (
    <div style={{ paddingBottom: '2rem' }}>
      {/* Date header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.75rem 1rem',
          borderBottom: '1px solid #27272a',
          position: 'sticky',
          top: 0,
          background: '#09090b',
          zIndex: 10,
        }}
      >
        <button
          onClick={() => setDate(addDays(date, -1))}
          style={navBtn}
          aria-label="Previous day"
        >
          ‹
        </button>
        <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{formatDate(date)}</span>
        <button
          onClick={() => setDate(addDays(date, 1))}
          disabled={isToday}
          style={{ ...navBtn, opacity: isToday ? 0.3 : 1 }}
          aria-label="Next day"
        >
          ›
        </button>
      </div>

      {isLoading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#71717a' }}>Loading…</div>
      ) : (
        <>
          {/* Calorie + macro summary */}
          <div
            style={{
              display: 'flex',
              gap: '1rem',
              alignItems: 'center',
              padding: '1rem',
              borderBottom: '1px solid #18181b',
            }}
          >
            <CalorieRing consumed={totals.calories} target={targets?.target_calories ?? null} />

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <MacroBar
                label="Protein"
                value={totals.protein_g}
                target={targets?.target_protein_g ?? null}
                color="#22d3ee"
              />
              <MacroBar
                label="Carbs"
                value={totals.carbs_g}
                target={targets?.target_carbs_g ?? null}
                color="#a78bfa"
              />
              <MacroBar
                label="Fat"
                value={totals.fat_g}
                target={targets?.target_fat_g ?? null}
                color="#fb923c"
              />
              <MacroBar
                label="Fibre"
                value={totals.fibre_g}
                target={null}
                color="#4ade80"
              />
            </div>
          </div>

          {/* Meal sections */}
          {MEALS.map((meal) => {
            const items: any[] = data?.by_meal?.[meal] ?? []
            const mealCals = items.reduce((s: number, i: any) => s + i.calories, 0)

            return (
              <div key={meal} style={{ borderBottom: '1px solid #18181b' }}>
                {/* Meal header */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.6rem 1rem',
                    background: '#0f0f11',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      {MEAL_LABELS[meal]}
                    </span>
                    {mealCals > 0 && (
                      <span style={{ fontSize: '0.75rem', color: '#71717a' }}>
                        {Math.round(mealCals)} kcal
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => navigate(`/log?meal=${meal}&date=${date}`)}
                    style={addBtn}
                    aria-label={`Add food to ${meal}`}
                  >
                    + Add
                  </button>
                </div>

                {/* Food items */}
                {items.length === 0 ? (
                  <div
                    style={{ padding: '0.5rem 1rem', fontSize: '0.8rem', color: '#3f3f46' }}
                  >
                    Nothing logged
                  </div>
                ) : (
                  items.map((item: any) => (
                    <div
                      key={item.log_id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.55rem 1rem',
                        borderTop: '1px solid #18181b',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: '0.85rem',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {item.name}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: '#71717a' }}>
                          {item.servings} × {item.serving_size_g}g
                          {' · '}P {item.protein_g}g · C {item.carbs_g}g · F {item.fat_g}g
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginLeft: '0.5rem' }}>
                        <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                          {Math.round(item.calories)}
                        </span>
                        <button
                          onClick={() => deleteLog.mutate(item.log_id)}
                          style={deleteBtn}
                          aria-label="Delete"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )
          })}

          {/* Water tracker */}
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #18181b' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '0.6rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span style={{ fontSize: '1rem' }}>💧</span>
                <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>Water</span>
              </div>
              <span style={{ fontSize: '0.85rem', color: '#71717a' }}>
                {waterMl} / {targets?.target_water_ml ?? 2000} ml
              </span>
            </div>
            <div style={{ height: 4, background: '#27272a', borderRadius: 2, overflow: 'hidden', marginBottom: '0.6rem' }}>
              <div
                style={{
                  height: '100%',
                  width: `${Math.min((waterMl / (targets?.target_water_ml ?? 2000)) * 100, 100)}%`,
                  background: '#38bdf8',
                  borderRadius: 2,
                  transition: 'width 0.4s ease',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {[250, 500, 600].map((ml) => (
                <button
                  key={ml}
                  onClick={() => addWater.mutate(ml)}
                  style={waterBtn}
                >
                  +{ml}ml
                </button>
              ))}
              <div style={{ display: 'flex', flex: 1, gap: '0.35rem' }}>
                <input
                  type="number"
                  value={customWater}
                  onChange={(e) => setCustomWater(e.target.value)}
                  placeholder="ml"
                  min="1"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    background: '#0c1e2e',
                    border: '1px solid #1e3a4f',
                    borderRadius: 6,
                    color: '#38bdf8',
                    fontSize: '0.8rem',
                    padding: '0.35rem 0.4rem',
                    outline: 'none',
                    textAlign: 'center',
                  }}
                />
                <button
                  onClick={() => {
                    const ml = parseInt(customWater)
                    if (ml > 0) {
                      addWater.mutate(ml)
                      setCustomWater('')
                    }
                  }}
                  disabled={!customWater || parseInt(customWater) <= 0}
                  style={{
                    ...waterBtn,
                    flex: 'none',
                    padding: '0.35rem 0.5rem',
                    opacity: !customWater || parseInt(customWater) <= 0 ? 0.4 : 1,
                  }}
                >
                  +
                </button>
              </div>
            </div>
          </div>

          {/* Weight tracker */}
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #18181b' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '0.6rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span style={{ fontSize: '1rem' }}>⚖️</span>
                <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>Weight</span>
              </div>
              {todayWeight && (
                <span style={{ fontSize: '0.85rem', color: '#a1a1aa' }}>
                  {todayWeight.weight_kg} kg logged
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="number"
                value={weightInput}
                onChange={(e) => setWeightInput(e.target.value)}
                placeholder={todayWeight ? `Update (${todayWeight.weight_kg} kg)` : 'kg'}
                min="20"
                max="300"
                step="0.1"
                style={{
                  flex: 1,
                  background: '#18181b',
                  border: '1px solid #27272a',
                  borderRadius: 6,
                  color: '#fafafa',
                  fontSize: '0.85rem',
                  padding: '0.4rem 0.6rem',
                  outline: 'none',
                }}
              />
              <button
                onClick={() => {
                  const kg = parseFloat(weightInput)
                  if (kg > 0) logWeight.mutate(kg)
                }}
                disabled={!weightInput || parseFloat(weightInput) <= 0 || logWeight.isPending}
                style={{
                  background: '#27272a',
                  border: '1px solid #3f3f46',
                  borderRadius: 6,
                  color: '#fafafa',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  padding: '0.4rem 0.75rem',
                  cursor: !weightInput || parseFloat(weightInput) <= 0 ? 'not-allowed' : 'pointer',
                  opacity: !weightInput || parseFloat(weightInput) <= 0 ? 0.4 : 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {todayWeight ? 'Update' : 'Log'}
              </button>
            </div>
          </div>

          {/* Footer attribution */}
          <div style={{ padding: '1rem', fontSize: '0.65rem', color: '#3f3f46', textAlign: 'center' }}>
            Nutrient data sourced from AUSNUT 2023, © Food Standards Australia New Zealand
          </div>
        </>
      )}
    </div>
  )
}

// Shared styles
const navBtn: CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#fafafa',
  fontSize: '1.5rem',
  cursor: 'pointer',
  padding: '0 0.5rem',
  lineHeight: 1,
}

const addBtn: CSSProperties = {
  background: 'none',
  border: '1px solid #27272a',
  borderRadius: 6,
  color: '#22d3ee',
  fontSize: '0.75rem',
  padding: '0.2rem 0.5rem',
  cursor: 'pointer',
}

const deleteBtn: CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#52525b',
  cursor: 'pointer',
  fontSize: '0.75rem',
  padding: '0.25rem',
  lineHeight: 1,
}

const waterBtn: CSSProperties = {
  flex: 1,
  background: '#0c1e2e',
  border: '1px solid #1e3a4f',
  borderRadius: 6,
  color: '#38bdf8',
  fontSize: '0.8rem',
  padding: '0.35rem 0',
  cursor: 'pointer',
}
